'use client';

import type { GroupedItems } from '../../api/queries';
import type { OmBannerItem, ProductUnitItem, UniversalSearchHeaderRequestBody } from '@weberus/shared-types';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { useAtomValue } from 'jotai';
import { EmptyState } from '@yanolja-rnd/nol-design-system';
import { waitUntilClickTimeout } from '@weberus/utils';
import { useMemo, useEffect } from 'react';
import { InView } from 'react-intersection-observer';
import { get, isObject } from 'lodash-es';
import { CriteoTracker } from '@weberus/client-tracker';
import DotLoader from '@/app/_components/common/dot-loader';
import { cn } from '@/app/_shared/utils/tw-cn';

import { OmBanner } from '@/app/_components/feature/om-banner';
import { RelatedKeyword } from '@/app/_components/feature/related-keyword';
import { Failover, NoFilterResult, NoKeywordSearchResult, NotFound } from '@/app/_components/common/empty-state';
import { SectionGroupHeader } from '@/app/_components/feature/section-group-header';
import { SearchSpellCorrection } from '@/app/_components/feature/search-spell-correction';
import { ProductUnitGroup } from '@/app/(pages)/results/ui/product-list/product-unit-group';
import { RecommendList } from '@/app/(pages)/results/ui/product-list/recommend-list';
import { ResponseError } from '@/app/_shared/utils/fetcher';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import {
  globalFilterParamsAtom,
  quickFilterAtom,
  searchFilterKeywordAtom,
  searchFilterSortAtom,
  sessionKeyAtom,
} from '@/app/_shared/store/search-filter';
import { useScrollRestoration } from '@/app/(pages)/results/hooks/use-scroll-restoration';
import { selectedVerticalTabInfoAtom } from '@/app/_shared/store/universal-search';
import {
  capacityAdultAtom,
  capacityChildAgesAtom,
  checkInDateAtom,
  checkOutDateAtom,
} from '@/app/_shared/store/local-accommodation';
import { globalCheckIOCapacityAtom } from '@/app/_shared/store/global-accommodation';
import { hashedMemberNoAtom } from '@/app/_shared/store/member';
import { localLocationAtom } from '@/app/_shared/store/location';
import { ProductListPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-web-txtpb';
import { getCategoryQueryValueFromKey } from '@/app/(pages)/results/utils/get-category-query-value-from-key';
import { testProductAtom } from '@/app/_shared/store/device';
import { isFeatureEnabled } from '@/app/_shared/utils/is-feature-enabled';
import { RecommendWidget } from '@/app/(pages)/results/ui/recommend-widget';
import { useSearchListQuery, useRecommendedLocalAccommodation, useRecommendedLocalLeisure } from '../../api/queries';
import { buildCriteoLogPayload } from '../../utils/build-criteo-log-payload';

// 로그를 위한 position 계산 로직
const calculateListPosition = (items: GroupedItems[], upToIndex = items.length) => {
  return items
    .slice(0, upToIndex)
    .filter((item) => Array.isArray(item) || (item.type !== 'SECTION_GROUP_HEADER' && item.type !== 'SECTION_DIVIDER'))
    .reduce((acc, cur) => acc + (Array.isArray(cur) ? cur.length : 1), 0);
};

/** Value for Client log */
// results/ui/product-list/index.tsx 참조
const LOG_MAPPING = {
  LOCAL_ACCOMMODATION: ProductListPageLog.SearchCardPlaceNo,
  LOCAL_LEISURE: ProductListPageLog.SearchCardLeisureTicketNo,
  GLOBAL_ACCOMMODATION: ProductListPageLog.SearchCardPropertyMasterId,
  ENTERTAINMENT: ProductListPageLog.SearchCardProductId,
} as const;

interface UniversalSearchProductListProps {
  memberId?: string;
}
export const UniversalSearchProductList = ({ memberId }: UniversalSearchProductListProps) => {
  const selectedTabInfo = useAtomValue(selectedVerticalTabInfoAtom);
  const quickFilter = useAtomValue(quickFilterAtom);
  const sessionKey = useAtomValue(sessionKeyAtom);
  const checkInDate = useAtomValue(checkInDateAtom);
  const checkOutDate = useAtomValue(checkOutDateAtom);
  const capacityAdults = useAtomValue(capacityAdultAtom);
  const childrenAges = useAtomValue(capacityChildAgesAtom);
  const globalCheckIOCapacity = useAtomValue(globalCheckIOCapacityAtom);
  const hashedMemberNo = useAtomValue(hashedMemberNoAtom);
  const sort = useAtomValue(searchFilterSortAtom);
  const location = useAtomValue(localLocationAtom);
  const keyword = useAtomValue(searchFilterKeywordAtom);
  const filters = useAtomValue(globalFilterParamsAtom);
  const testProduct = useAtomValue(testProductAtom);

  // 검색홈 1.0 Feature 플래그
  const isUniversalSearchV1 = isFeatureEnabled('universalSearchV1');

  const category = useMemo(() => selectedTabInfo?.key, [selectedTabInfo]);

  const {
    filteredList,
    lastItemRef,
    isDataLoading,
    isError,
    isFirstPageLoaded,
    hasNextPage,
    error,
    refetch,
    fetchNextPage,
    noProductResult,
    spellCorrection,
    serverLogMeta,
  } = useSearchListQuery({
    headers: testProduct ? { 'test-product': testProduct } : {},
    params: {
      keyword,
      filters: filters || [],
      category,
      sort: sort || 'RECOMMEND',
      userLocation: {
        latitude: Number(location?.latitude),
        longitude: Number(location?.longitude),
        locationType: location?.locationType,
        locationTime: location?.locationTime,
      },
      localAccommodation: {
        checkInDate,
        checkOutDate,
        capacityAdults,
        childrenAges,
      },
      globalAccommodation: {
        checkInDate: globalCheckIOCapacity.checkInDate,
        checkOutDate: globalCheckIOCapacity.checkOutDate,
        rooms: globalCheckIOCapacity.capacityRooms.map((room) => ({
          capacityAdults: room.adult,
          childrenAges: room.child,
        })),
      },
    } as UniversalSearchHeaderRequestBody,
    category: category || 'LOCAL_ACCOMMODATION',
    hashedMemberNo,
  });

  const {
    header: localAccommodationHeader,
    items: localAccommodationItems,
    isLoading: isAccommodationLoading,
  } = useRecommendedLocalAccommodation({
    keyword,
    checkInDate: checkInDate!,
    checkOutDate: checkOutDate!,
    capacityAdults,
    childrenAges,
    enabled: category === 'LOCAL_ACCOMMODATION',
  });
  const {
    header: localLeisureHeader,
    items: localLeisureItems,
    isLoading: isLeisureLoading,
  } = useRecommendedLocalLeisure({
    keyword,
    enabled: category === 'LOCAL_ACCOMMODATION' || (category === 'LOCAL_LEISURE' && !!noProductResult),
    recommendType: category === 'LOCAL_LEISURE' ? 'LOCAL_LEISURE_SRP_NO_RESULT' : 'LOCAL_ACCOMMODATION_SRP',
  });

  useScrollRestoration(sessionKey, getCategoryQueryValueFromKey(category)); // 스크롤 저장 및 복원 로직 사용

  const isLastItemProductItemType = Array.isArray(filteredList[filteredList.length - 1]);
  const isVisibleRecommendLocalAccommodationList =
    !!localAccommodationItems.length &&
    !isDataLoading &&
    !isAccommodationLoading &&
    noProductResult &&
    localAccommodationItems.length > 1;
  const isVisibleRecommendLocalLeisureList =
    !!localLeisureItems.length &&
    !isDataLoading &&
    !isLeisureLoading &&
    !isAccommodationLoading &&
    !hasNextPage &&
    localLeisureItems.length > 1;

  const recommendLocalListPosition = calculateListPosition(filteredList);
  const recommendLeisureListPosition = recommendLocalListPosition + localAccommodationItems.length;

  useEffect(() => {
    if (!isFirstPageLoaded) return;

    const payload = buildCriteoLogPayload({
      memberId,
      category,
      checkInDate,
      checkOutDate,
      capacityAdults,
      childrenAges,
      globalCheckIOCapacity,
      keyword,
      filteredList,
    });
    CriteoTracker.push(payload);
  }, [isFirstPageLoaded]);

  const handleOmBannerClick = (position: number, item: OmBannerItem) => {
    clientLogProtobuf(ProductListPageLog.SearchBannerOm.click, {
      logMeta: {
        position,
        ...serverLogMeta,
        ...item.serverLogMeta,
      },
    })();

    item?.data?.action && navigateTo(item.data.action);
  };

  const handleOmBannerImpr = (position: number, item: OmBannerItem) => {
    clientLogProtobuf(ProductListPageLog.SearchBannerOm.impression, {
      logMeta: {
        position,
        ...serverLogMeta,
        ...item.serverLogMeta,
      },
    })();
  };

  const handleItemImpression = (item: ProductUnitItem, index: number, listPosition: number) => {
    const logHandler = LOG_MAPPING[category as keyof typeof LOG_MAPPING];
    if (!logHandler) {
      return;
    }

    const log = {
      logMeta: {
        ...item.serverLogMeta,
        ...(category === 'LOCAL_ACCOMMODATION' && {
          objectsWith: [
            {
              adSlot: item.serverLogMeta?.adSlot || undefined,
              adSlotId: item.serverLogMeta?.adSlotId || undefined,
              adSeq: item.serverLogMeta?.adSeq || undefined,
              adTags: item.serverLogMeta?.adTags || undefined,
              placeNo: item.serverLogMeta?.id,
              position: listPosition + index + 1,
              positionInSlot: item.serverLogMeta?.positionInSlot || index + 1,
            },
          ],
        }),
      },
      position: listPosition + index + 1, // TODO: serverDriven 으로 들어오는 position 을 무시 해야하므로, serverLogMeta 후에 정의 할 것
    };
    clientLogProtobuf(logHandler.impression, log)();
  };

  const handleItemClick = async (item: ProductUnitItem, index: number, listPosition: number) => {
    const logHandler = LOG_MAPPING[category as keyof typeof LOG_MAPPING];
    if (logHandler) {
      const log = {
        logMeta: item.serverLogMeta,
        placeNo: item.data.id,
        position: listPosition + index + 1,
      };

      await waitUntilClickTimeout(logHandler.click(log));
    }

    navigateTo(item?.data?.action);
  };

  const renderRecommendLists = () => {
    if (category === 'GLOBAL_ACCOMMODATION') return null;

    const noResult = filteredList.length === 0 && !isDataLoading && isFirstPageLoaded;

    return (
      <>
        {/* 숙소 추천 목록 */}
        {category === 'LOCAL_ACCOMMODATION' && isVisibleRecommendLocalAccommodationList && (
          <RecommendList
            header={localAccommodationHeader?.data}
            items={localAccommodationItems}
            position={recommendLocalListPosition}
            isNoResult={noResult}
            serverLogMeta={serverLogMeta}
          />
        )}
        {/* 레저 추천 목록 */}
        {isVisibleRecommendLocalLeisureList && (
          <RecommendList
            header={localLeisureHeader?.data}
            items={localLeisureItems}
            isNoResult={noResult}
            position={recommendLeisureListPosition}
            serverLogMeta={serverLogMeta}
          />
        )}
      </>
    );
  };

  const renderRecommend = () => {
    if (category === 'GLOBAL_ACCOMMODATION') return null;

    const noResult = filteredList.length === 0 && !isDataLoading && isFirstPageLoaded;
    const commonProps = { isNoResult: noResult, serverLogMeta };

    // 레저 추천에서 사용할 컴포넌트 결정
    const LeisureComponent = category === 'LOCAL_LEISURE' ? RecommendList : RecommendWidget;

    return (
      <>
        {/* 숙소 추천 */}
        {category === 'LOCAL_ACCOMMODATION' && isVisibleRecommendLocalAccommodationList && (
          <RecommendList
            header={localAccommodationHeader?.data}
            items={localAccommodationItems}
            position={recommendLocalListPosition}
            {...commonProps}
          />
        )}

        {/* 레저 추천 */}
        {isVisibleRecommendLocalLeisureList && (
          <LeisureComponent
            header={localLeisureHeader?.data}
            items={localLeisureItems}
            position={recommendLeisureListPosition}
            {...commonProps}
          />
        )}
      </>
    );
  };

  const spellCorrectionBanner = () => {
    if (!spellCorrection) return null;

    return (
      <SearchSpellCorrection
        keyword={spellCorrection}
        onImpression={() => {
          clientLogProtobuf(ProductListPageLog.SearchLabelSpellCorrectionMsg.impression, {
            keyword: spellCorrection,
            logMeta: serverLogMeta,
          })();
        }}
        onClick={() => {
          clientLogProtobuf(ProductListPageLog.SearchLabelSpellCorrectionMsg.click, {
            keyword: spellCorrection,
            logMeta: serverLogMeta,
          })();
        }}
      />
    );
  };

  const noSearchResult = () => {
    return (
      <>
        <div className="px-16 pc:px-0">{spellCorrectionBanner()}</div>
        <div
          className={cn(
            !spellCorrection &&
              !isVisibleRecommendLocalAccommodationList &&
              !isVisibleRecommendLocalLeisureList &&
              'mt-40',
          )}>
          <NoKeywordSearchResult
            title="일치하는 상품이 없어요"
            description="지역명, 상품명으로 검색어를 변경해 보세요"
            onImpression={() => {
              // 국내숙소, 레저는 헤더에서 전송
              if (category === 'GLOBAL_ACCOMMODATION') {
                clientLogProtobuf(ProductListPageLog.SearchLabelNoAvailableProductMsg.impression, {
                  position: 0,
                  logMeta: serverLogMeta,
                })();
              }
            }}
          />
        </div>
      </>
    );
  };

  /* 네트워크 에러 및 페이지 조회 실패 노출 */
  if (isError && !isFirstPageLoaded) {
    if (error instanceof ResponseError && error && error?.statusCode >= 500) {
      return <Failover refetch={refetch} description="네트워크 상태를 확인하고, 다시 시도해 주세요." />;
    }
    return <NotFound refetch={refetch} />;
  }

  /* 검색 결과 없음 노출 - 필터 결과 없음 | 키워드 결과 없음 | 카테고리 검색 결과 없음 */
  if (!filteredList.length && !isDataLoading && isFirstPageLoaded) {
    return (
      <div className="flex w-full max-w-legacy-pc-size flex-col pc:px-16">
        {quickFilter?.hasSelectedFilter ? (
          <InView
            triggerOnce
            onChange={(inView) => {
              // 국내숙소, 레저는 헤더에서 전송
              if (inView && category === 'GLOBAL_ACCOMMODATION') {
                clientLogProtobuf(ProductListPageLog.SearchLabelNoAvailableProductMsg.impression, {
                  position: 0,
                  logMeta: serverLogMeta,
                })();
              }
            }}>
            <NoFilterResult />
          </InView>
        ) : (
          <>
            {noSearchResult()}
            {isUniversalSearchV1 ? renderRecommend() : renderRecommendLists()}
          </>
        )}
      </div>
    );
  }

  /* 키워드 검색 결과 없음 및 OM 배너 노출 - SRP 검색 결과가 OM 배너 하나일 경우 */
  if (
    noProductResult &&
    filteredList.length === 1 &&
    !Array.isArray(filteredList[0]) &&
    filteredList[0].type === 'ONSITE_MARKETING_BANNER'
  ) {
    const item = filteredList[0];
    return (
      <div className="flex w-full max-w-legacy-pc-size flex-col pc:px-16">
        {noSearchResult()}
        <div className="border-t border-line-neutral-weak2">
          <OmBanner
            {...item.data}
            onClick={() => handleOmBannerClick(1, item)}
            onImpression={() => handleOmBannerImpr(1, item)}
          />
        </div>
        {isUniversalSearchV1 ? renderRecommend() : renderRecommendLists()}
      </div>
    );
  }

  if (!isFirstPageLoaded && isDataLoading) {
    return (
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
        <DotLoader />
      </div>
    );
  }

  return (
    <>
      {spellCorrection && (
        <div className="flex w-full max-w-legacy-pc-size flex-col bg-static-white px-16">{spellCorrectionBanner()}</div>
      )}
      <div
        className={cn(
          'flex w-full max-w-legacy-pc-size flex-col bg-static-white pc:px-16 [&>*:first-child]:border-t-0',
          { 'border-b-[1px] border-line-neutral-weak1 pc:border-b-0': !isLastItemProductItemType },
          {
            'pc:[&>:first-child]:mt-20':
              filteredList?.length > 0 &&
              isObject(filteredList[0]) &&
              get(filteredList[0], 'type') !== 'ONSITE_MARKETING_BANNER',
          },
        )}>
        {filteredList.map((item, index) => {
          const listPosition = calculateListPosition(filteredList, index);
          /* 상품 목록 */
          if (Array.isArray(item)) {
            return (
              <ProductUnitGroup
                key={index}
                items={item}
                listPosition={listPosition}
                onImpression={handleItemImpression}
                onClick={handleItemClick}
              />
            );
          }
          if (item.type === 'ONSITE_MARKETING_BANNER') {
            return (
              <OmBanner
                key={index}
                {...item.data}
                onClick={() => handleOmBannerClick(listPosition, item)}
                onImpression={() => handleOmBannerImpr(listPosition, item)}
              />
            );
          }
          /* 연관 검색어 */
          if (item.type === 'RELATED_KEYWORD') {
            return (
              <RelatedKeyword
                key={index}
                title={item.data.title}
                items={item.data.items}
                onImpression={() => {
                  clientLogProtobuf(ProductListPageLog.RelatedKeywordsLabel.impression, {
                    logMeta: {
                      ...serverLogMeta,
                      ...item?.serverLogMeta,
                    },
                  })();
                }}
                onClick={(keyword: string) => {
                  clientLogProtobuf(ProductListPageLog.RelatedKeywordsLabelSelectedKeyword.click, {
                    position: listPosition,
                    selectedKeyword: keyword,
                    logMeta: {
                      ...serverLogMeta,
                      ...item?.serverLogMeta,
                    },
                  })();
                }}
              />
            );
          }
          /* 섹션 그룹 헤더 */
          if (item.type === 'SECTION_GROUP_HEADER') {
            return <SectionGroupHeader key={index} id={index} title={item.data?.title} tooltip={item.data?.tooltip} />;
          }
          /* 섹션 구분 */
          if (item.type === 'SECTION_DIVIDER') {
            return <div key={index} className="h-8 bg-fill-neutral-weak1 pc:h-20 pc:bg-transparent" />;
          }
          return null;
        })}
        <div ref={lastItemRef} />
        {/* 페이지 조회 실패 - 무한 스크롤 페이지 에러 */}
        {isError && (
          <div className="flex h-full w-full justify-center bg-static-white">
            <EmptyState
              title="화면을 불러오지 못했어요"
              pcType="pcLegacySize"
              buttons={[{ content: '다시 시도하기', onClick: () => fetchNextPage() }]}
            />
          </div>
        )}
        {isUniversalSearchV1 ? renderRecommend() : renderRecommendLists()}
        {isDataLoading && (
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
            <DotLoader />
          </div>
        )}
      </div>
    </>
  );
};
